import { useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  CaseIntent,
  DemoCaseDetail,
  DemoCorrectionData,
  Priority,
  SenderSegment,
} from '../../api/demo';

export interface DemoReviewFormValues {
  correction_data: DemoCorrectionData;
  notes: string;
}

interface DemoReviewFormProps {
  detail: DemoCaseDetail;
  canApprove: boolean;
  saving: boolean;
  onApprove: (values: DemoReviewFormValues) => void;
  onCorrect: (values: DemoReviewFormValues) => void;
}

const SEGMENTS: Array<{ value: SenderSegment; label: string }> = [
  { value: 'owner', label: 'Proprietario' },
  { value: 'referring_vet', label: 'Veterinario inviante' },
  { value: 'specialist', label: 'Specialista' },
  { value: 'administrative', label: 'Amministrativo' },
];

const PRIORITIES: Array<{ value: Priority; label: string }> = [
  { value: 'low', label: 'Bassa' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

const INTENTS: Array<{ value: CaseIntent; label: string }> = [
  { value: 'second_opinion', label: 'Secondo parere' },
  { value: 'surgery_candidacy', label: 'Candidabilità chirurgica' },
  { value: 'quote_availability', label: 'Preventivo e disponibilità' },
  { value: 'appointment_followup', label: 'Follow-up appuntamento' },
  { value: 'urgent_consult', label: 'Consulto urgente' },
  { value: 'general_information', label: 'Informazioni generali' },
  { value: 'therapy_question', label: 'Domanda su terapia' },
  { value: 'administrative', label: 'Amministrativo' },
];

function readFirst<T>(...values: Array<T | undefined | null>): T | undefined {
  for (const value of values) {
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
}

function initialSegment(detail: DemoCaseDetail): SenderSegment | '' {
  const source = detail.review?.correction_data ?? {};
  const analysis = (detail.clinical.analysis ?? {}) as Record<string, unknown>;
  const expected = (detail.clinical.expected ?? {}) as Record<string, unknown>;
  return (
    readFirst(
      source.sender_segment,
      analysis.sender_segment as SenderSegment | undefined,
      expected.sender_segment as SenderSegment | undefined,
    ) ?? ''
  );
}

function initialPriority(detail: DemoCaseDetail): Priority | '' {
  const source = detail.review?.correction_data ?? {};
  const analysis = (detail.clinical.analysis ?? {}) as Record<string, unknown>;
  const expected = (detail.clinical.expected ?? {}) as Record<string, unknown>;
  return (
    readFirst(
      source.priority,
      analysis.priority as Priority | undefined,
      expected.priority as Priority | undefined,
    ) ?? ''
  );
}

function initialSpecialty(detail: DemoCaseDetail): string {
  const source = detail.review?.correction_data ?? {};
  const analysis = (detail.clinical.analysis ?? {}) as Record<string, unknown>;
  const expected = (detail.clinical.expected ?? {}) as Record<string, unknown>;
  return readFirst(
    source.specialty,
    analysis.specialty as string | undefined,
    expected.specialty as string | undefined,
  ) ?? '';
}

function initialIntents(detail: DemoCaseDetail): CaseIntent[] {
  const source = detail.review?.correction_data ?? {};
  const analysis = (detail.clinical.analysis ?? {}) as Record<string, unknown>;
  const expected = (detail.clinical.expected ?? {}) as Record<string, unknown>;
  return (
    readFirst(
      source.intents,
      analysis.intents as CaseIntent[] | undefined,
      expected.intents as CaseIntent[] | undefined,
    ) ?? []
  );
}

function initialMissingInformation(detail: DemoCaseDetail): string[] {
  const source = detail.review?.correction_data ?? {};
  const analysis = (detail.clinical.analysis ?? {}) as Record<string, unknown>;
  const expected = (detail.clinical.expected ?? {}) as Record<string, unknown>;
  return (
    readFirst(
      source.missing_information,
      analysis.missing_information as string[] | undefined,
      expected.missing_information as string[] | undefined,
    ) ?? []
  );
}

/**
 * Form di review: segment, intents, priority, specialty, missing information,
 * notes e revision.
 *
 * Lo stato dei campi e' locale e viene inizializzato una sola volta. Un
 * conflitto di revisione (409) non smonta il form: il testo digitato resta
 * esattamente dov'e', pronto per essere ritentato dopo il refresh.
 */
export function DemoReviewForm({
  detail,
  canApprove,
  saving,
  onApprove,
  onCorrect,
}: DemoReviewFormProps) {
  const [segment, setSegment] = useState<SenderSegment | ''>(() => initialSegment(detail));
  const [intents, setIntents] = useState<CaseIntent[]>(() => initialIntents(detail));
  const [priority, setPriority] = useState<Priority | ''>(() => initialPriority(detail));
  const [specialty, setSpecialty] = useState<string>(() => initialSpecialty(detail));
  const [missingInformation, setMissingInformation] = useState<string>(() =>
    initialMissingInformation(detail).join('\n'),
  );
  const [notes, setNotes] = useState<string>(() => detail.review?.notes ?? '');

  const revision = detail.review?.revision ?? 0;

  const toggleIntent = (intent: CaseIntent) => {
    setIntents((current) =>
      current.includes(intent)
        ? current.filter((value) => value !== intent)
        : [...current, intent],
    );
  };

  const buildValues = (): DemoReviewFormValues => {
    const correction_data: DemoCorrectionData = {};
    if (segment) correction_data.sender_segment = segment;
    if (intents.length > 0) correction_data.intents = intents;
    if (priority) correction_data.priority = priority;
    if (specialty.trim()) correction_data.specialty = specialty.trim();
    const missing = missingInformation
      .split('\n')
      .map((value) => value.trim())
      .filter(Boolean);
    if (missing.length > 0) correction_data.missing_information = missing;
    return { correction_data, notes };
  };

  return (
    <form
      data-testid="demo-review-form"
      onSubmit={(event) => event.preventDefault()}
      className="space-y-4"
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="demo-review-segment">Segmento</Label>
          <select
            id="demo-review-segment"
            aria-label="Segmento"
            value={segment}
            onChange={(event) => setSegment(event.target.value as SenderSegment | '')}
            className="h-8 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            <option value="">—</option>
            {SEGMENTS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="demo-review-priority">Priorità</Label>
          <select
            id="demo-review-priority"
            aria-label="Priorità"
            value={priority}
            onChange={(event) => setPriority(event.target.value as Priority | '')}
            className="h-8 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            <option value="">—</option>
            {PRIORITIES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5 md:col-span-2">
          <Label htmlFor="demo-review-specialty">Specialità</Label>
          <Input
            id="demo-review-specialty"
            aria-label="Specialità"
            value={specialty}
            onChange={(event) => setSpecialty(event.target.value)}
            placeholder="Specialità dell'analisi"
          />
        </div>
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-foreground">Intenti</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {INTENTS.map((option) => {
            const checked = intents.includes(option.value);
            return (
              <label
                key={option.value}
                className="flex items-center gap-2 text-sm text-foreground"
              >
                <input
                  type="checkbox"
                  className="size-4 rounded border-input accent-primary"
                  checked={checked}
                  onChange={() => toggleIntent(option.value)}
                />
                {option.label}
              </label>
            );
          })}
        </div>
      </fieldset>

      <div className="space-y-1.5">
        <Label htmlFor="demo-review-missing">Informazioni mancanti</Label>
        <Textarea
          id="demo-review-missing"
          aria-label="Informazioni mancanti"
          value={missingInformation}
          onChange={(event) => setMissingInformation(event.target.value)}
          placeholder="Una per riga"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="demo-review-notes">Note</Label>
        <Textarea
          id="demo-review-notes"
          aria-label="Note"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Note di revisione"
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground">
          Revisione attesa:{' '}
          <span data-testid="demo-review-revision" className="font-mono text-foreground">
            {revision}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={saving}
            onClick={() => onCorrect(buildValues())}
          >
            <Check className="size-3.5" />
            Salva correzione
          </Button>
          <Button
            type="button"
            disabled={!canApprove || saving}
            title={canApprove ? undefined : 'Approvazione non disponibile per i casi falliti'}
            onClick={() => onApprove(buildValues())}
          >
            Approva
          </Button>
        </div>
      </div>
    </form>
  );
}
